import React from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { PPC_TABS } from '@css-xp/ppc-common';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { getFilterOptionsForInvoiceGroupsQuery } from '@css-xp/ppc-utils';
import { maxThresholdForResults } from '@css-xp/ppc-constants';
import {
  fetchInvoices,
  fetchCloudSites,
  fetchEmails,
  fetchPartnerProductEntitlements,
  fetchOrdersForTransactionAccountInPPC,
  fetchInvoiceGroupsForSearch,
  searchQuotesQueryForPPC,
  fetchBillingProfilesForPPC,
  fetchRefundsInPPC,
} from '../queries/fetch-total-counts-for-search-page';

interface useTotalCountQueryTypes {
  queryString: string;
  txaId: string;
  partnerSearchContext: string;
  isOrgSearchEnabled?: boolean;
  partnerAccountId?: string;
  getTabsQueryVariables?: Record<string, () => void>;
}

const parentKey = 'transactionAccount';

const whereFilterProperties = [
  'name',
  'nameText',
  'shipToName',
  'shipToNameText',
  'shipToAddress1',
  'shipToAddress2',
  'shipToCity',
  'shipToCityText',
  'shipToState',
  'shipToStateText',
  'shipToCountry',
  'shipToPostalCode',
];

const buildFilterOptionsForBillingProfile = (searchTerm: string) => {
  const value = whereFilterProperties.map((property) => {
    return { [property]: searchTerm };
  });
  return [
    {
      key: 'or',
      value: JSON.stringify(value),
    },
  ];
};

const getVariablesForOrders = async ({
  isOrgSearchEnabled,
  txaId,
  queryString,
  client,
}: {
  isOrgSearchEnabled?: boolean;
  client: ApolloClient<NormalizedCacheObject>;
  txaId: string;
  queryString: string;
}) => {
  const { data } = await client?.query({
    query: fetchInvoiceGroupsForSearch,
    variables: {
      transactionAccountId: txaId,
      searchOptions: {
        first: maxThresholdForResults,
        offset: 0,
      },
      filterOptions: getFilterOptionsForInvoiceGroupsQuery(txaId, queryString),
      isOrgSearchEnabled,
    },
  });
  const invoiceGroups = data?.invoiceGroups?.nodes ?? [];
  if (!invoiceGroups.length) {
    return null;
  }
  const orFilter = invoiceGroups.map((invoiceGroup: { id: string }) => {
    return {
      key: 'billingGroupId',
      value: invoiceGroup?.id,
    };
  });

  return {
    transactionAccountId: txaId,
    searchOptions: {
      first: 1,
      offset: 0,
    },
    filterOptions: [
      {
        and: [
          {
            or: orFilter,
          },
        ],
      },
    ],
  };
};

export const useTotalCountQueryConfig = (data: useTotalCountQueryTypes) => {
  const {
    queryString,
    txaId,
    partnerSearchContext,
    isOrgSearchEnabled,
    partnerAccountId,
    getTabsQueryVariables,
  } = data;
  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const sxpClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);
  const queryConfigs = React.useMemo(
    () => [
      {
        tabName: PPC_TABS.SUBSCRIPTIONS,
        query: fetchPartnerProductEntitlements,
        client: ansClient,
        resultKey: 'partnerProductEntitlements',
        variables: {
          searchParams: {
            query: queryString,
            partnerSearchContext,
            transactionAccountId: txaId,
            isOrgSearchEnabled,
          },
        },
      },
      {
        tabName: PPC_TABS.INVOICES,
        query: fetchInvoices,
        resultKey: 'partnerInvoices',
        parentKey,
        client: ansClient,
        variables: {
          id: txaId,
          partnerSearchContext,
          queryString,
        },
      },
      {
        tabName: PPC_TABS.SITE_URLS,
        query: fetchCloudSites,
        resultKey: 'partnerCloudSites',
        client: ansClient,
        variables: {
          id: txaId,
          searchParams: {
            query: queryString,
            transactionAccountId: txaId,
            partnerSearchContext,
            isOrgSearchEnabled,
          },
        },
      },
      {
        tabName: PPC_TABS.EMAILS,
        query: fetchEmails,
        parentKey,
        resultKey: 'emails',
        client: ansClient,
        variables: {
          id: txaId,
          partnerAccountId,
          searchParams: {
            query: queryString,
          },
        },
      },
      {
        tabName: PPC_TABS.ORDERS,
        query: fetchOrdersForTransactionAccountInPPC,
        parentKey,
        resultKey: 'orders',
        client: ansClient,
        getVariables: () => getVariablesForOrders({ isOrgSearchEnabled, txaId, queryString, client: ansClient }),
      },
      {
        tabName: PPC_TABS.QUOTES,
        query: searchQuotesQueryForPPC,
        resultKey: 'quotes',
        client: sxpClient,
        getVariables: () => getTabsQueryVariables?.[PPC_TABS.QUOTES](),
      },

      {
        tabName: PPC_TABS.BILLING_PROFILES,
        query: fetchBillingProfilesForPPC,
        parentKey,
        resultKey: 'billingProfiles',
        client: ansClient,
        variables: {
          id: txaId,
          searchOptions: {
            first: 1,
            offset: 0,
          },
          filterOptions: buildFilterOptionsForBillingProfile(queryString),
        },
      },
      {
        tabName: PPC_TABS.REFUNDS,
        query: fetchRefundsInPPC,
        resultKey: 'partnerRefunds',
        client: ansClient,
        variables: {
          searchParams: {
            query: queryString,
            transactionAccountId: txaId,
            partnerSearchContext,
            isOrgSearchEnabled,
          },
        },
        getTotalCount: (result: any) => result?.partnerRefunds?.results?.length,
      },
    ],
    [queryString]
  );

  return { queryConfigs };
};
